import React, { forwardRef, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Chip, Paper, Stack, Typography } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import { SEVERITY } from '../makeReady/severity'
import LoadListSelect from '../makeReadyModule/LoadListSelect'
import { loadListsForKind } from '../makeReadyModule/useLoadLists'
import BoardLocation, { groupMeta } from './BoardLocation'

const UNTRACKED_HEX = SEVERITY.untracked.hex

// A vehicle/asset with no load list. Lives on the board alongside tracked
// cards so the gap is visible, and lets the user close it in place: pick a
// list (the server's suggestion is pre-selected), assign, run the first check.
const UntrackedItemCard = forwardRef(({ item, loadLists, onAssigned }, ref) => {
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [selected, setSelected] = useState(item.suggestedLoadList?._id || '')
  const [saving, setSaving] = useState(false)

  // Fall back to the only/first matching list if there's no suggestion.
  useEffect(() => {
    if (selected) return
    const options = loadListsForKind(loadLists, item.kind)
    if (options.length === 1) setSelected(options[0]._id)
  }, [loadLists, item.kind, selected])

  const { Icon, singular } = groupMeta(item.group)
  const hasOptions = loadListsForKind(loadLists, item.kind).length > 0

  const handleAssign = () => {
    if (!selected || saving) return
    setSaving(true)
    API.post('/makeready/assign-load-list', {
      targetId: item.id,
      loadListId: selected,
    })
      .then((res) => {
        if (res.status === 200) {
          onAssigned?.(item.id, res.data?.loadList || null)
          navigate(`/make-ready/${item.id}`)
        } else {
          setToast({
            message:
              res.data?.error ||
              'Unable to assign that load list. Please try again.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to assign that load list. Please try again.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  return (
    <Paper
      ref={ref}
      elevation={1}
      sx={{
        borderLeft: `6px dashed ${UNTRACKED_HEX}`,
        overflow: 'hidden',
        transition: 'box-shadow .15s ease',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <Stack sx={{ p: 1.5 }} gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Icon sx={{ color: UNTRACKED_HEX }} />
            <Stack>
              <Typography fontWeight={700}>{item.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {item.subLabel}
              </Typography>
            </Stack>
          </Stack>
          <Chip
            size="small"
            label={SEVERITY.untracked.label}
            sx={{
              bgcolor: '#F1F5F9',
              color: '#64748B',
              border: `1px dashed ${UNTRACKED_HEX}`,
              fontWeight: 600,
            }}
          />
        </Stack>

        <BoardLocation location={item.location} />

        <Typography variant="body2" color="text.secondary">
          No load list assigned yet, so this {singular} isn&apos;t being
          checked.
        </Typography>

        <LoadListSelect
          kind={item.kind}
          loadLists={loadLists}
          value={selected}
          onChange={(id) => setSelected(id || '')}
          suggestedLoadList={item.suggestedLoadList}
          disabled={saving}
        />

        {hasOptions && (
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!selected || saving}
            onClick={handleAssign}
          >
            {saving ? 'Assigning…' : 'Assign and run first check'}
          </Button>
        )}
      </Stack>
    </Paper>
  )
})

UntrackedItemCard.displayName = 'UntrackedItemCard'

export default UntrackedItemCard
